// ============================================================================
// Wie eine Zeile der Auftragsinfo ihren Verfasser nennt.
//
// ORIGINALTEXT MARVIN (08.09.2026)
//   „Wochenplan: Popup am Auftrag zeigt die Auftragsinfo anders (zweimal
//    «System», keine Bilder) als die Akte."
//
// DIE ANTWORT STAND ZWEIMAL IM HAUS, UND EINE DER BEIDEN WAR ROH. Die Akte
// hatte eine Übersetzung (`QUELLE`) und einen Rückfall auf den Verfasser; das
// Popup schrieb den Verfasser UND den rohen Aufzählungswert hintereinander:
//
//     Akte     „Marvin"                    (Name, sonst „System")
//     Popup    „Marvin, person"           (Name UND Quelle)
//              „System, system"           - daher „zweimal System"
//
// SIE STEHT JETZT EINMAL, UND OHNE JSX DANEBEN. Eine Regel, die nur in einer
// Datei mit Bildschirmcode stünde, liesse sich mit
// `--experimental-strip-types` nicht laden - und dann bliebe die Textsuche.
// **Eine Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.**
// Vierzehnte Datei dieser Art nach `namen.ts`, `terminraster.ts`,
// `klebeband.ts`, `kartenmass.ts`, `oberflaeche.ts`, `kartenebene.ts`,
// `grenzpunkte.ts`, `handbuchmarken.ts`, `tabellenbeschriftung.ts`,
// `zahlwoerter.ts`, `bruchstellen.ts`, `anlasstext.ts`, `technikfelder.ts`
// und `grabenlaenge.ts`.
// ============================================================================

/**
 * Die Werte von `ereignis_quelle`, für Menschen.
 *
 * Sie sind Bezeichner und bleiben ASCII, wie jeder Aufzählungswert. Im
 * Verlauf stand bis zum 26.08.2026 der ROHE Wert, sobald kein Name daneben
 * lag: der Monteur las „person" neben seinem eigenen Satz.
 */
export const QUELLE: Record<string, string> = {
  system: 'System',
  // Kein „Innendienst": wer es war, steht in `verfasser_name`, und wenn der
  // fehlt, wissen wir es nicht. Eine geratene Abteilung wäre eine Behauptung.
  person: 'von Hand',
  telekom: 'Telekom',
  gbgs: 'GBGS',
  bot: 'Automatik',
};

/**
 * Wer da steht: der Name, sonst die Quelle in Worten, sonst der rohe Wert.
 *
 * DER ROHE WERT WIRD NICHT VERSCHLUCKT. Kommt mit einer Migration ein Wert
 * dazu, den diese Liste nicht kennt, steht er da, wie er ist. Ein leeres
 * Feld fällt niemandem auf; ein unbekanntes Wort schon - und dann trägt es
 * jemand nach.
 */
export function werSteht(
  verfasserName: string | null | undefined,
  quelle: string | null | undefined,
  kurz: (n: string) => string = (n) => n
): string {
  const n = String(verfasserName ?? '').trim();
  if (n) return kurz(n);
  const q = String(quelle ?? '').trim();
  return QUELLE[q] ?? q;
}

/**
 * Und die Quelle steht NUR dann daneben, wenn sie etwas hinzufügt.
 *
 * Bei einem Eintrag mit Verfasser sagt sie nichts, was der Name nicht schon
 * sagt - „Marvin, von Hand" ist eine Wiederholung. Bei einem Eintrag ohne
 * Verfasser IST sie der Name und steht dann schon in `werSteht`; ein
 * zweites Mal daneben wäre genau das „zweimal System" der Beanstandung.
 */
export function quelleDaneben(
  verfasserName: string | null | undefined,
  quelle: string | null | undefined
): string | null {
  const n = String(verfasserName ?? '').trim();
  const q = String(quelle ?? '').trim();
  if (!n || !q) return null;
  // Ein Mensch hat geschrieben - „von Hand" sagt dazu nichts.
  if (q === 'person') return null;
  // Ein Name AN einer maschinellen Quelle ist eine Auskunft: er sagt, wer
  // den Lauf ausgelöst hat, und die Quelle sagt, dass es ein Lauf war.
  return QUELLE[q] ?? q;
}
